import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import useSEO from "@/hooks/useSEO";
import { BreadcrumbSchema } from "@/components/SEOStructuredData";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useRecaptcha } from "@/hooks/useRecaptcha";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SkipToContent from "@/components/SkipToContent";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Mail, Phone, MapPin, User, X } from "lucide-react";
import ObfuscatedContact from "@/components/ObfuscatedContact";

interface LocationState {
  talentName?: string;
  talentId?: string;
  talentSlug?: string;
}

interface PageContent {
  section_key: string;
  content_en: string;
  content_fr: string;
  is_hidden: boolean;
  display_order: number;
}

// Validation schema
const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().max(100, "Company must be less than 100 characters").optional(),
  service: z.string().optional(),
  genre: z.string().optional(),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters"),
});

const Contact = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  useEffect(() => {
    document.body.classList.add("show-recaptcha-badge");
    return () => document.body.classList.remove("show-recaptcha-badge");
  }, []);
  const { getToken } = useRecaptcha();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { localizedPath } = useLocalizedPath();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const locationState = location.state as LocationState | null;
  const [selectedTalent, setSelectedTalent] = useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(locationState?.talentName && locationState?.talentId && locationState?.talentSlug ? {
    name: locationState.talentName,
    id: locationState.talentId,
    slug: locationState.talentSlug,
  } : null);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    genre: "",
    message: "",
  });

  // Fetch all services content from database (titles and usecases)
  const { data: servicesData } = useQuery({
    queryKey: ["services-for-contact-full"],
    queryFn: async () => {
      const { data } = await supabase
        .from("page_content")
        .select("section_key, content_en, content_fr, is_hidden, display_order")
        .eq("page_slug", "services")
        .eq("is_hidden", false);
      return data as PageContent[] | null;
    },
  });

  // Build services list for dropdown
  const services = useMemo(() => {
    if (!servicesData) return [];
    return servicesData
      .filter(item => item.section_key.endsWith("_title"))
      .filter(item => {
        const prefix = item.section_key.replace("_title", "");
        // Exclude non-service sections
        return prefix !== "services" && prefix !== "cta" && prefix !== "hours";
      })
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(item => ({
        value: item.section_key.replace("_title", ""),
        label: currentLang === "fr" ? item.content_fr : item.content_en,
      }));
  }, [servicesData, currentLang]);

  // Build genres for selected service
  const genres = useMemo(() => {
    if (!formData.service || !servicesData) return [];
    const usecasesItem = servicesData.find(
      item => item.section_key === `${formData.service}_usecases`
    );
    if (!usecasesItem) return [];
    const content = currentLang === "fr" ? usecasesItem.content_fr : usecasesItem.content_en;
    return content
      .split("\n")
      .map(line => line.trim())
      .filter(line => line !== "")
      .map(line => ({
        value: line,
        label: line,
      }));
  }, [formData.service, servicesData, currentLang]);

  // Pre-select service from URL params
  useEffect(() => {
    const serviceParam = searchParams.get("service");
    
    if (serviceParam && services.length > 0) {
      const matchingService = services.find(s => s.value === serviceParam);
      if (matchingService && formData.service !== serviceParam) {
        setFormData(prev => ({ ...prev, service: serviceParam }));
      }
    }
  }, [searchParams, services, formData.service]);

  // Pre-select genre from URL params (after genres are available)
  useEffect(() => {
    const genreParam = searchParams.get("genre");
    
    if (genreParam && genres.length > 0 && !formData.genre) {
      const decodedGenre = decodeURIComponent(genreParam);
      const matchingGenre = genres.find(g => g.value === decodedGenre);
      if (matchingGenre) {
        setFormData(prev => ({ ...prev, genre: decodedGenre }));
      }
    }
  }, [searchParams, genres, formData.genre]);

  useSEO({
    title: t("contactPage.title"),
    description: t("seo.contactDescription", "Contact First Stage Talent to book professional voiceover artists and on-camera talent for your next project."),
  });

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: t("contactPage.title"), url: `https://firststagetalent.com/${currentLang}/contact` },
  ];

  // Pre-fill message when coming from a talent profile
  useEffect(() => {
    if (selectedTalent?.name) {
      setFormData((prev) => ({
        ...prev,
        message: t("contactPage.talentRequestMessage", { talentName: selectedTalent.name }) || `I'm interested in booking ${selectedTalent.name} for my project.`,
      }));
    }
  }, [selectedTalent?.name, t]);

  const handleClearTalent = () => {
    setSelectedTalent(null);
    setFormData((prev) => ({ ...prev, message: "" }));
    window.history.replaceState({}, document.title);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    try {
      // Validate form data
      const validatedData = contactSchema.parse(formData);

      // Verify reCAPTCHA
      const recaptchaToken = await getToken("contact_form");
      if (recaptchaToken) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-recaptcha", {
          body: { token: recaptchaToken, action: "contact_form" },
        });
        
        if (verifyError || !verifyData?.success) {
          console.error("reCAPTCHA verification failed:", verifyError || verifyData?.error);
          toast({
            title: t("contactPage.error", "Error"),
            description: t("common.captchaFailed", "Security verification failed. Please try again."),
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Build message with service and genre if selected
      let finalMessage = validatedData.message;
      const selectedService = services.find(s => s.value === validatedData.service);
      
      if (selectedService || validatedData.genre) {
        const prefix = [];
        if (selectedService) {
          prefix.push(`Service: ${stripHtml(selectedService.label)}`);
        }
        if (validatedData.genre) {
          prefix.push(`Genre: ${validatedData.genre}`);
        }
        finalMessage = `[${prefix.join(" | ")}]\n\n${finalMessage}`;
      }

      // Insert into database
      const { error } = await supabase
        .from("talent_requests")
        .insert({
          name: validatedData.name,
          email: validatedData.email,
          company: validatedData.company || null,
          phone: null,
          message: finalMessage,
          talent_id: selectedTalent?.id || null,
          status: "new",
        });

      if (error) {
        console.error("Form submission error:", error);
        toast({
          title: t("contactPage.error", "Error"),
          description: t("contactPage.errorDesc", "Failed to send message. Please try again."),
          variant: "destructive",
        });
        return;
      }

      // Send email notification (don't block on failure)
      try {
        await supabase.functions.invoke("send-contact-notification", {
          body: {
            name: validatedData.name,
            email: validatedData.email,
            company: validatedData.company || undefined,
            message: finalMessage,
            talentName: selectedTalent?.name || undefined,
          },
        });
      } catch (emailError) {
        console.error("Email notification error:", emailError);
        // Don't show error to user - the form was still saved successfully
      }

      toast({
        title: t("contactPage.messageSent"),
        description: t("contactPage.messageSentDesc"),
      });

      setFormData({ name: "", email: "", company: "", service: "", genre: "", message: "" });
      setSelectedTalent(null);
      window.history.replaceState({}, document.title);
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: t("contactPage.error", "Error"),
          description: t("contactPage.errorDesc", "Failed to send message. Please try again."),
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleServiceChange = (value: string) => {
    setFormData((prev) => ({ ...prev, service: value, genre: "" }));
  };

  const handleGenreChange = (value: string) => {
    setFormData((prev) => ({ ...prev, genre: value }));
  };

  // Strip HTML for display
  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <div className="min-h-screen">
      <BreadcrumbSchema items={breadcrumbs} />
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <PageHero 
          title={t("contactPage.title")} 
          subtitle={t("contactPage.subtitle")}
        />
        
        <section className="section-padding">
          <div className="container-wide">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
              {/* Form */}
              <ScrollReveal animation="slideLeft">
                {/* Selected Talent Banner */}
                {selectedTalent && (
                  <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                        <User size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t("contactPage.requestingTalent", "Requesting talent:")}</p>
                        <Link 
                          to={localizedPath(`/talent/${selectedTalent.slug}`)}
                          className="font-medium text-foreground hover:text-primary transition-colors"
                        >
                          {selectedTalent.name}
                        </Link>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearTalent}
                      className="p-1.5 hover:bg-muted rounded-full transition-colors"
                      aria-label={t("contactPage.clearTalent", "Clear selected talent")}
                    >
                      <X size={18} className="text-muted-foreground" />
                    </button>
                  </div>
                )}

              <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t("contactPage.name")} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        maxLength={100}
                        placeholder={t("contactPage.namePlaceholder")}
                        className={`bg-background ${errors.name ? "border-destructive" : ""}`}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "name-error" : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="text-sm text-destructive">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("contactPage.email")} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        maxLength={255}
                        placeholder={t("contactPage.emailPlaceholder")}
                        className={`bg-background ${errors.email ? "border-destructive" : ""}`}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">{t("contactPage.company")}</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      maxLength={100}
                      placeholder={t("contactPage.companyPlaceholder")}
                      className="bg-background"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">{t("contactPage.service", "Service")}</Label>
                    <Select value={formData.service} onValueChange={handleServiceChange}>
                      <SelectTrigger id="service" className="bg-background">
                        <SelectValue placeholder={t("contactPage.selectService", "Select a service")} />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50">
                        {services.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            {stripHtml(service.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="genre">{t("contactPage.genre", "Genre")}</Label>
                    <Select 
                      value={formData.genre} 
                      onValueChange={handleGenreChange}
                      disabled={!formData.service || genres.length === 0}
                    >
                      <SelectTrigger id="genre" className="bg-background">
                        <SelectValue placeholder={t("contactPage.selectGenre", "Select a genre")} />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50">
                        {genres.map((genre) => (
                          <SelectItem key={genre.value} value={genre.value}>
                            {genre.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t("contactPage.projectDetails")} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={2000}
                      placeholder={t("contactPage.projectDetailsPlaceholder")}
                      rows={6}
                      className={`bg-background resize-none ${errors.message ? "border-destructive" : ""}`}
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? "message-error" : undefined}
                    />
                    {errors.message && (
                      <p id="message-error" className="text-sm text-destructive">{errors.message}</p>
                    )}
                  </div>

                  <Button type="submit" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? t("contactPage.sending") : t("contactPage.sendMessage")}
                  </Button>
                  <p className="text-[11px] text-muted-foreground/70 text-center mt-6 leading-relaxed">
                    {t("common.recaptchaNotice", "This site is protected by reCAPTCHA and the Google")}{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      {t("common.privacyPolicy", "Privacy Policy")}
                    </a>{" "}
                    {t("common.and", "and")}{" "}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      {t("common.termsOfService", "Terms of Service")}
                    </a>{" "}
                    {t("common.apply", "apply")}.
                  </p>
                </form>

                <p className="text-sm text-muted-foreground mt-6">
                  {t("contactPage.responseNote")}
                </p>
              </ScrollReveal>

              {/* Contact Info */}
              <ScrollReveal animation="slideRight" className="lg:pl-8">
                <div className="bg-secondary p-8 md:p-10 space-y-8">
                  <div>
                    <h3 className="text-sm uppercase tracking-wide font-medium mb-6 text-primary">{t("contactPage.contactInfo")}</h3>
                    <ul className="space-y-6">
                      <li className="flex items-start gap-4">
                        <Mail size={20} className="text-primary mt-0.5" aria-hidden="true" />
                        <div>
                          <p className="font-medium">{t("footer.email")}</p>
                          <ObfuscatedContact 
                            type="email" 
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          />
                        </div>
                      </li>
                      <li className="flex items-start gap-4">
                        <Phone size={20} className="text-primary mt-0.5" aria-hidden="true" />
                        <div>
                          <p className="font-medium">{t("footer.phone")}</p>
                          <ObfuscatedContact 
                            type="phone" 
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          />
                        </div>
                      </li>
                      <li className="flex items-start gap-4">
                        <MapPin size={20} className="text-primary mt-0.5" aria-hidden="true" />
                        <div>
                          <p className="font-medium">{t("footer.location")}</p>
                          <p className="text-muted-foreground">
                            Ottawa-Gatineau<br />Ontario / Québec, Canada
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="pt-6 border-t border-border">
                    <h3 className="text-sm uppercase tracking-wide font-medium mb-4 text-primary">{t("contactPage.whatHappensNext")}</h3>
                    <ol className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <span className="w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                        <span>{t("contactPage.step1")}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                        <span>{t("contactPage.step2")}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                        <span>{t("contactPage.step3")}</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;